import { clsx } from 'clsx'

import { Member, Post } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { usePost } from '@tribeplatform/react-sdk/hooks'
import { SvgIcon, type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { Time } from '../common/components/Time.js'
import { dayjs } from '../common/lib/dayjs.js'
import { logger } from '../common/lib/logger.js'
import { T } from '../i18n/components/T.js'

export const parseLog = log => {
  const payload = log.payload.reduce((props, keyValue) => {
    try {
      props[keyValue.key] = JSON.parse(keyValue?.value)
    } catch (err) {
      logger.error('Cannot parse activity log value')
    }
    return props
  }, {})

  return {
    payload: { ...payload },
    entities: log.entities,
  }
}

export const getLogIcon = (
  noun: string,
): { icon: SvgIconName; color: string } => {
  const mapping: Record<string, { icon: SvgIconName; color: string }> = {
    SPACE: {
      icon: 'block',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    SPACE_MEMBERSHIP: {
      icon: 'block',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    SPACE_JOIN_REQUEST: {
      icon: 'block',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    POST: {
      icon: 'edit',
      color: 'bg-decorative-surface-3 text-decorative-content-3',
    },
    MEMBER: {
      icon: 'member',
      color: 'bg-decorative-surface-1 text-decorative-content-1',
    },
    NETWORK: {
      icon: 'globe',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    PLAN: {
      icon: 'globe',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    SUBSCRIPTION: {
      icon: 'globe',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    COLLECTION: {
      icon: 'collection',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    REACTION: {
      icon: 'heart',
      color: 'bg-decorative-surface-1 text-decorative-content-1',
    },
    MODERATION: {
      icon: 'moderation',
      color: 'bg-decorative-surface-2 text-decorative-content-2',
    },
    MEMBER_INVITATION: {
      icon: 'mail',
      color: 'bg-decorative-surface-1 text-decorative-content-1',
    },
    MEMBER_SESSION: {
      icon: 'key',
      color: 'bg-decorative-surface-1 text-decorative-content-1',
    },
    APP_INSTALLATION: {
      icon: 'app',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    TAG: {
      icon: 'tag',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    APP: {
      icon: 'app',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    SHORTCUT: {
      icon: 'app',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    SSO: {
      icon: 'key',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
    BADGE: {
      icon: 'badge-check',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    },
  }

  return (
    mapping[noun] || {
      icon: 'help',
      color: 'bg-decorative-surface-5 text-decorative-content-5',
    }
  )
}

export const LogMemberLink = ({ member }: { member: Member }) => {
  const { Link } = useRouter()

  if (member?.relativeUrl) {
    return <Link href={member?.relativeUrl}>{member?.name}</Link>
  }

  return (
    <span>
      <T
        id="AuditLog.Fallback.Someone"
        defaultMessage="Someone"
        description="Actor name fallback"
      />
    </span>
  )
}

export const LogActorLink = ({
  payload,
  actor,
}: {
  payload
  actor: Member
}) => {
  const { Link } = useRouter()

  if (payload?.actorId?.includes('SUPPORT')) {
    return (
      <span>
        <T id="AuditLog.Fallback.Support" defaultMessage="Support" />
      </span>
    )
  }

  if (actor?.relativeUrl) {
    return <Link href={actor?.relativeUrl}>{actor?.name}</Link>
  }
  return (
    <span>
      <T
        id="AuditLog.Fallback.Someone"
        defaultMessage="Someone"
        description="Actor name fallback"
      />
    </span>
  )
}

interface ExtractPostId {
  noun: string
  object: {
    id?: string
    postId?: string
    entityId?: string
  }
  parentPost?: Post
}
const extractPostId = ({ noun, object, parentPost }: ExtractPostId) => {
  if (noun === 'POST') {
    return object?.postId ?? object?.id
  }

  if (noun === 'MODERATION') {
    return object?.entityId
  }

  if (noun === 'REACTION') {
    return object?.postId
  }

  return parentPost?.id
}

export const LogPostLink = ({
  payload,
  post: parentPost,
}: {
  payload
  post: Post
}) => {
  const { Link } = useRouter()

  const noun = payload?.noun
  const verb = payload?.verb
  const object = payload?.object
  const parentPostId = payload?.postId ?? parentPost?.id

  const objectPostId = extractPostId({ noun, object, parentPost })

  // b/e was supposed to provide the entity and fetching the post is a hot-fix burying a deeper issue that we have in the b/e.
  const { data: objectPost } = usePost({
    variables: {
      id: objectPostId,
    },
    useQueryOptions: {
      enabled: !!objectPostId && !parentPost?.relativeUrl,
    },
  })

  const isReply = !!parentPostId && objectPostId !== parentPostId
  if (isReply) {
    const isFirstLevelReply = object?.repliedToId === parentPostId

    if (isFirstLevelReply && ['PUBLISHED', 'UNPUBLISHED'].includes(verb)) {
      return (
        <Link href={`${parentPost?.relativeUrl}?highlight=${objectPostId}`}>
          {parentPost?.title || (
            <T
              id="AuditLog.Fallback.APost"
              defaultMessage="a post"
              description="Post title fallback"
            />
          )}
        </Link>
      )
    }

    return (
      <Link href={`${parentPost?.relativeUrl}?highlight=${objectPostId}`}>
        <T
          id="AuditLog.Fallback.ReplyLinkText"
          defaultMessage="reply"
          description="Reply link fallback in audit logs"
        />
      </Link>
    )
  }

  return (
    <Link href={parentPost?.relativeUrl || objectPost?.relativeUrl}>
      {parentPost?.title || objectPost?.title || object?.title || (
        <T
          id="AuditLog.Fallback.APost"
          defaultMessage="a post"
          description="Post title fallback"
        />
      )}
    </Link>
  )
}

export const AuditLogRow = ({ log: { payload, entities } }) => {
  const { Link } = useRouter()

  const noun = payload?.noun
  const verb = payload?.verb

  if (
    !noun ||
    !verb ||
    [
      'SPACE_NETWORK_ROLE',
      'ROLE',
      'SPACE_ROLE',
      'POST_TYPE',
      'SPACE_POST_TYPE',
    ].indexOf(noun) !== -1
  ) {
    return null
  }

  const action = `${noun}.${verb}`
  const { icon, color: iconColor } = getLogIcon(noun)
  const { actor, member, app, space, post } = entities
  const object = payload?.object
  const actorLink = <LogActorLink payload={payload} actor={actor} />
  const spaceLink = space?.slug ? (
    <Link href={`/${space?.slug}`}>{space?.name}</Link>
  ) : (
    <T
      id="AuditLog.Fallback.ASpace"
      defaultMessage="a {SPACE}"
      description="Space name fallback in audit logs in case the name was missing"
    />
  )
  const memberLink = <LogMemberLink member={member} />
  const postLink = <LogPostLink payload={payload} post={post} />

  const fallBackAppName = (
    <T
      id="AuditLog.Fallback.AnApp"
      defaultMessage="an app"
      description="App name fallback in audit logs"
    />
  )

  // @TODO: Remove this in a week
  if (actor?.name === 'Deleted Member' || member?.name === 'Deleted Member') {
    return null
  }

  let sentence = (
    <>
      {actorLink} {verb?.toLowerCase()} {noun}
    </>
  )
  switch (action) {
    case 'APP_INSTALLATION.CREATED':
      sentence = (
        <T
          id="AuditLog.InstalledApp"
          defaultMessage="{actor_link} installed {app_name}"
          values={{
            actor_link: actorLink,
            app_name: app?.name ?? fallBackAppName,
          }}
        />
      )
      break

    case 'APP_INSTALLATION.DELETED':
      sentence = (
        <T
          id="AuditLog.UninstalledApp"
          defaultMessage="{actor_link} uninstalled {app_name}"
          values={{
            actor_link: actorLink,
            app_name: app?.name ?? fallBackAppName,
          }}
        />
      )
      break

    case 'APP.CREATED':
      sentence = (
        <T
          id="AuditLog.CreatedApp"
          defaultMessage="{actor_link} created {app_name}"
          values={{
            actor_link: actorLink,
            app_name: app?.name ?? 'fallBackAppName',
          }}
        />
      )
      break

    case 'APP.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedApp"
          defaultMessage="{actor_link} updated {app_name} app settings"
          values={{ actor_link: actorLink, app_name: object?.name }}
        />
      )
      break

    case 'BADGE.CREATED':
      sentence = (
        <T
          id="AuditLog.CreatedBadge"
          defaultMessage="{actor_link} created {badge_name} badge"
          values={{ actor_link: actorLink, badge_name: object?.name }}
        />
      )
      break

    case 'BADGE.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedBadge"
          defaultMessage="{actor_link} updated {badge_name} badge"
          values={{ actor_link: actorLink, badge_name: object?.name }}
        />
      )
      break

    case 'MEMBER.CREATED':
      sentence = (
        <T
          id="AuditLog.Joined"
          defaultMessage="{actor_link} joined the {COMMUNITY}"
          values={{ actor_link: <LogMemberLink member={object} /> }}
        />
      )
      break

    case 'MEMBER.UPDATED':
      if (object?.id === actor?.id) {
        sentence = (
          <T
            id="AuditLog.UpdatedSelfProfile"
            defaultMessage="{actor_link} updated their profile"
            values={{ actor_link: actorLink }}
          />
        )
      } else {
        sentence = (
          <T
            id="AuditLog.UpdateOtherProfile"
            defaultMessage="{actor_link} updated {member_link}’s profile"
            values={{ actor_link: actorLink, member_link: memberLink }}
          />
        )
      }
      break

    case 'MEMBER.DELETED':
      sentence = (
        <T
          id="AuditLog.DeletedMember"
          defaultMessage="{actor_link} deleted {member_link}’s account"
          values={{ actor_link: actorLink, member_link: memberLink }}
        />
      )
      break

    case 'MEMBER.SUSPENDED':
      sentence = (
        <T
          id="AuditLog.SuspendedMember"
          defaultMessage="{actor_link} suspended {member_link}’s account"
          values={{ actor_link: actorLink, member_link: memberLink }}
        />
      )
      break

    case 'MEMBER.UNSUSPENDED':
      sentence = (
        <T
          id="AuditLog.UnsuspendedMember"
          defaultMessage="{actor_link} unsuspended {member_link}’s account"
          values={{ actor_link: actorLink, member_link: memberLink }}
        />
      )
      break

    case 'MEMBER.VERIFIED':
      sentence = (
        <T
          id="AuditLog.VerifiedAccount"
          defaultMessage="{actor_link} verified their account"
          values={{ actor_link: actorLink }}
        />
      )
      break

    case 'MEMBER_INVITATION.CREATED':
      sentence = (
        <T
          id="AuditLog.SentMemberInvitation"
          defaultMessage="{actor_link} sent invitation to {invitee_email}"
          values={{
            actor_link: actorLink,
            invitee_email: object?.inviteeEmail,
          }}
        />
      )
      break

    case 'MEMBER_INVITATION.UPDATED':
      sentence = (
        <T
          id="AuditLog.ResentMemberInvitation"
          defaultMessage="{actor_link} re-sent a member invitation"
          values={{ actor_link: actorLink }}
        />
      )
      break

    case 'MODERATION.CREATED':
      if (object?.entityType === 'POST') {
        sentence = (
          <T
            id="AuditLog.GotFlagged"
            defaultMessage="{actor_link}’s {post_link} was flagged for moderation"
            values={{ actor_link: actorLink, post_link: postLink }}
          />
        )
      }
      break

    case 'PLAN.UPDATED':
      sentence = (
        <T
          id="AuditLog.PlanUpdated"
          defaultMessage="{COMMUNITY} plan was updated"
        />
      )
      break

    case 'SUBSCRIPTION.CREATED':
      sentence = (
        <T
          id="AuditLog.SubscriptionCreated"
          defaultMessage="a new {COMMUNITY} subscription was created"
        />
      )
      break

    case 'SUBSCRIPTION.UPDATED':
      sentence = (
        <T
          id="AuditLog.SubscriptionUpdated"
          defaultMessage="{COMMUNITY_CC} subscription was updated"
        />
      )
      break

    case 'SSO.CREATED':
      sentence = (
        <T
          id="AuditLog.EnabledSso"
          defaultMessage="{actor_link} enabled SSO"
          values={{ actor_link: actorLink }}
        />
      )
      break

    case 'TAG.ADDED':
      sentence = (
        <T
          id="AuditLog.CreatedTag"
          defaultMessage="{actor_link} added {tag_name} {TAG}"
          values={{ actor_link: actorLink, tag_name: object?.title }}
        />
      )
      break

    case 'TAG.REMOVED':
      sentence = (
        <T
          id="AuditLog.TagRemoved"
          defaultMessage="{actor_link} removed {tag_name} {TAG}"
          values={{ actor_link: actorLink, tag_name: object?.title }}
        />
      )
      break

    case 'MODERATION.REJECTED':
      if (object?.entityType === 'POST') {
        sentence = (
          <T
            id="AuditLog.ApprovedPost"
            defaultMessage="{actor_link} approved {post_link}"
            values={{ actor_link: actorLink, post_link: postLink }}
          />
        )
      }
      break

    case 'MODERATION.ACCEPTED':
      if (object?.entityType === 'POST') {
        sentence = (
          <T
            id="AuditLog.RejectedPost"
            defaultMessage="{actor_link} rejected {post_link}"
            values={{ actor_link: actorLink, post_link: postLink }}
          />
        )
      }
      break

    case 'NETWORK.CREATED':
      sentence = (
        <T
          id="AuditLog.CreatedNetwork"
          defaultMessage="{actor_link} created this {COMMUNITY}"
          values={{ actor_link: actorLink }}
        />
      )
      break

    case 'NETWORK.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedNetwork"
          defaultMessage="{actor_link} updated {COMMUNITY} settings"
          values={{ actor_link: actorLink }}
        />
      )
      break

    case 'POST.CREATED':
    case 'POST.BLOCKED':
    case 'POST.UNBLOCKED':
    case 'CUSTOM.CREATED':
      return null

    case 'POST.FOLLOWED':
      sentence = (
        <T
          id="AuditLog.FollowedPost"
          defaultMessage="{actor_link} followed {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break

    case 'POST.UNFOLLOWED':
      sentence = (
        <T
          id="AuditLog.UnfollowedPost"
          defaultMessage="{actor_link} unfollowed {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break

    case 'POST.HIDDEN':
      sentence = (
        <T
          id="AuditLog.HidPost"
          defaultMessage="{actor_link} hid {post_link}"
          values={{
            actor_link: actorLink,
            post_link: postLink,
          }}
        />
      )
      break

    case 'POST.UNHIDDEN':
      sentence = (
        <T
          id="AuditLog.UnhidPost"
          defaultMessage="{actor_link} unhid {post_link}"
          values={{
            actor_link: actorLink,
            post_link: postLink,
          }}
        />
      )
      break

    case 'POST.PUBLISHED':
      if (object?.repliedToId) {
        sentence = (
          <T
            id="AuditLog.Replied"
            defaultMessage="{actor_link} replied to {post_link}"
            values={{ actor_link: actorLink, post_link: postLink }}
          />
        )
      } else {
        sentence = (
          <T
            id="AuditLog.Posted"
            defaultMessage="{actor_link} posted {post_link}"
            values={{ actor_link: actorLink, post_link: postLink }}
          />
        )
      }
      break

    case 'POST.UNPUBLISHED':
      if (object?.repliedToId) {
        sentence = (
          <T
            id="AuditLog.UnpublishedReply"
            defaultMessage="{actor_link} removed reply from {post_link}"
            values={{
              actor_link: actorLink,
              post_link: postLink,
            }}
          />
        )
      } else {
        sentence = (
          <T
            id="AuditLog.UnpublishedPost"
            defaultMessage="{actor_link} deleted {post_link} post"
            values={{
              actor_link: actorLink,
              post_link: postLink,
            }}
          />
        )
      }
      break
    case 'POST.DELETED':
      return null

    case 'POST.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedPost"
          defaultMessage="{actor_link} updated {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break
    case 'POST.PINNED':
      sentence = (
        <T
          id="AuditLog.PinnedPost"
          defaultMessage="{actor_link} pinned {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break
    case 'POST.UNPINNED':
      sentence = (
        <T
          id="AuditLog.UnpinnedPost"
          defaultMessage="{actor_link} unpinned {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break
    case 'REACTION.ADDED':
      sentence = (
        <T
          id="AuditLog.AddedReaction"
          defaultMessage="{actor_link} added reaction to {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break

    case 'REACTION.REMOVED':
      sentence = (
        <T
          id="AuditLog.RemovedReaction"
          defaultMessage="{actor_link} removed reaction from {post_link}"
          values={{ actor_link: actorLink, post_link: postLink }}
        />
      )
      break
    case 'SPACE.CREATED':
      sentence = (
        <T
          id="AuditLog.CreatedSpace"
          defaultMessage="{actor_link} created {space_link}"
          values={{ actor_link: actorLink, space_link: spaceLink }}
        />
      )
      break

    case 'SPACE.DELETED':
      sentence = (
        <T
          id="AuditLog.DeletedSpace"
          defaultMessage="{actor_link} deleted {space_name} {SPACE}"
          values={{ actor_link: actorLink, space_name: object?.name }}
        />
      )
      break

    case 'SPACE.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedSpace"
          defaultMessage="{actor_link} updated {space_link}"
          values={{ actor_link: actorLink, space_link: spaceLink }}
        />
      )
      break

    case 'SPACE_MEMBERSHIP.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedSpaceMembership"
          defaultMessage="{actor_link} updated {member_link}’s role in {space_link}"
          values={{
            actor_link: actorLink,
            space_link: spaceLink,
            member_link: memberLink,
          }}
        />
      )
      break

    case 'COLLECTION.CREATED':
      sentence = (
        <T
          id="AuditLog.CreatedCollection"
          defaultMessage="{actor_link} created {collection_link} {COLLECTION}"
          values={{
            actor_link: actorLink,
            collection_link: (
              <Link href={`/collection/${object?.id}`}>{object?.name}</Link>
            ),
          }}
        />
      )
      break

    case 'COLLECTION.DELETED':
      sentence = (
        <T
          id="AuditLog.DeletedCollection"
          defaultMessage="{actor_link} deleted {collection_name} {COLLECTION}"
          values={{
            actor_link: actorLink,
            collection_name: object?.name,
          }}
        />
      )
      break

    case 'COLLECTION.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedCollection"
          defaultMessage="{actor_link} updated {collection_link} {COLLECTION}"
          values={{
            actor_link: actorLink,
            collection_link: (
              <Link href={`/collection/${object?.id}`}>{object?.name}</Link>
            ),
          }}
        />
      )
      break

    case 'SPACE_MEMBERSHIP.CREATED':
      if (member?.id === actor?.id) {
        sentence = (
          <T
            id="AuditLog.JoinedSpace"
            defaultMessage="{actor_link} joined {space_link}"
            values={{
              actor_link: actorLink,
              space_link: spaceLink,
            }}
          />
        )
      } else {
        sentence = (
          <T
            id="AuditLog.InvitedToSpace"
            defaultMessage="{actor_link} added {member_link} to {space_link}"
            values={{
              actor_link: actorLink,
              space_link: spaceLink,
              member_link: memberLink,
            }}
          />
        )
      }
      break

    case 'SPACE_MEMBERSHIP.DELETED':
      if (member?.id === actor?.id) {
        sentence = (
          <T
            id="AuditLog.LeftSpace"
            defaultMessage="{actor_link} left {space_link}"
            values={{
              actor_link: actorLink,
              space_link: spaceLink,
            }}
          />
        )
      } else {
        sentence = (
          <T
            id="AuditLog.RemovedFromSpace"
            defaultMessage="{actor_link} removed {member_link} from {space_link}"
            values={{
              actor_link: actorLink,
              space_link: spaceLink,
              member_link: memberLink,
            }}
          />
        )
      }
      break

    case 'SPACE_JOIN_REQUEST.CREATED':
      sentence = (
        <T
          id="AuditLog.RequestedToJoinSpace"
          defaultMessage="{actor_link} requested to join {space_link}"
          values={{
            actor_link: actorLink,
            space_link: spaceLink,
          }}
        />
      )
      break

    case 'SPACE_JOIN_REQUEST.ACCEPTED':
      sentence = (
        <T
          id="AuditLog.AcceptedRequestToJoinSpace"
          defaultMessage="{actor_link} accepted {member_link}’s join request to {space_link}"
          values={{
            actor_link: actorLink,
            space_link: spaceLink,
            member_link: memberLink,
          }}
        />
      )
      break

    case 'SSO.UPDATED':
      sentence = (
        <T
          id="AuditLog.UpdatedSso"
          defaultMessage="{actor_link} updated {COMMUNITY} SSO settings"
          values={{ actor_link: actorLink }}
        />
      )
      break

    default:
  }

  if (!sentence) {
    return null
  }

  return (
    <List.Item className="flex items-center space-s-3">
      <div
        className={clsx('rounded-button text-surface p-2 shrink-0', iconColor)}
      >
        <SvgIcon name={icon} size="lg" />
      </div>
      <div className="min-w-0 grow">
        <div className="line-clamp-2">{sentence}</div>
        <Time dateTime={payload.createdAt} className="text-sm">
          {dayjs(payload.createdAt).fromNow()}
        </Time>
      </div>
    </List.Item>
  )
}
